'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef } from 'react';
import { useWebHaptics } from 'web-haptics/react';
import type { ClothingCategory, ClothingItem } from '@/types/closet';
interface OutfitRegionsProps {
  onRegionClick: (category: ClothingCategory) => void;
  selectedItems: Partial<Record<ClothingCategory, ClothingItem>>;
  disabled?: boolean;
  playSound?: (type: 'hover' | 'click' | 'switch' | 'success') => void;
}
interface Region {
  category: ClothingCategory;
  label: string;
  color: string;
  path?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}
const REGIONS: Region[] = [{
  category: 'outerwear',
  label: 'JACKET',
  color: '#EF4444',
  x: 15,
  y: 25,
  width: 25,
  height: 15
}, {
  category: 'tops',
  label: 'TOPS',
  color: '#EF4444',
  x: 15,
  y: 40,
  width: 25,
  height: 20
}, {
  category: 'bottoms',
  label: 'BOTTOMS',
  color: '#3B82F6',
  x: 60,
  y: 55,
  width: 30,
  height: 25,
  // Custom path for pants shape
  path: 'M 40 55 L 50 55 L 52 65 L 48 80 L 45 80 L 47 65 L 43 65 L 45 80 L 42 80 L 38 65 L 40 55 Z'
}, {
  category: 'footwear',
  label: 'SHOES',
  color: '#22C55E',
  x: 25,
  y: 85,
  width: 20,
  height: 10
}, {
  category: 'accessories',
  label: 'ACCESSORIES',
  color: '#F59E0B',
  x: 65,
  y: 35,
  width: 25,
  height: 15
}];
const BLUR_STYLE_SMALL = {
  filter: 'blur(2px)'
} as const;
const BLUR_STYLE_LARGE = {
  filter: 'blur(4px)'
} as const;
const NO_BLUR_STYLE = {
  filter: 'none'
} as const;
const BACKDROP_STYLE = {
  backdropFilter: 'blur(8px)'
} as const;
export function OutfitRegions(t0) {
  const $ = _c(33);
  const {
    onRegionClick,
    selectedItems,
    disabled,
    playSound
  } = t0;
  const haptic = useWebHaptics();
  const [hoveredRegion, setHoveredRegion] = useState(null);
  const supportsHover = useRef(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      supportsHover.current = window.matchMedia("(hover: hover)").matches;
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== disabled || $[3] !== haptic || $[4] !== onRegionClick || $[5] !== playSound) {
    t3 = category => {
      if (!disabled) {
        haptic.trigger("light");
        playSound?.("click");
        onRegionClick(category);
      }
    };
    $[2] = disabled;
    $[3] = haptic;
    $[4] = onRegionClick;
    $[5] = playSound;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleClick = t3;
  let labelStyles;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[7] !== disabled || $[8] !== handleClick || $[9] !== hoveredRegion) {
    const handleMouseEnter = category_0 => {
      if (!supportsHover.current) {
        return;
      }
      if (!disabled) {
        setHoveredRegion(() => category_0);
      }
    };
    let t9;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = () => {
        setHoveredRegion(null);
      };
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    const handleMouseLeave = t9;
    let t10;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = REGIONS.reduce(_temp, {} as Record<ClothingCategory, React.CSSProperties>);
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    labelStyles = t10;
    t8 = "absolute inset-0";
    t4 = "size-full";
    t5 = "0 0 100 100";
    t6 = "none";
    t7 = REGIONS.map(region_0 => <g key={region_0.category}>{region_0.path ? <><path d={region_0.path} fill={hoveredRegion === region_0.category ? `${region_0.color}30` : "transparent"} stroke="transparent" className="transition-[fill,filter] duration-200" style={hoveredRegion === region_0.category ? BLUR_STYLE_SMALL : NO_BLUR_STYLE} /><path d={region_0.path} fill="transparent" className={`cursor-pointer ${disabled ? "cursor-not-allowed opacity-50" : ""}`} onClick={() => handleClick(region_0.category)} onMouseEnter={() => handleMouseEnter(region_0.category)} onMouseLeave={handleMouseLeave} /></> : region_0.x !== undefined && region_0.y !== undefined && region_0.width && region_0.height && <><rect x={region_0.x} y={region_0.y} width={region_0.width} height={region_0.height} fill={hoveredRegion === region_0.category ? `${region_0.color}20` : "transparent"} stroke="transparent" className="transition-[fill,filter] duration-200" style={hoveredRegion === region_0.category ? BLUR_STYLE_LARGE : NO_BLUR_STYLE} /><rect x={region_0.x} y={region_0.y} width={region_0.width} height={region_0.height} fill="transparent" className={`cursor-pointer ${disabled ? "cursor-not-allowed opacity-50" : ""}`} onClick={() => handleClick(region_0.category)} onMouseEnter={() => handleMouseEnter(region_0.category)} onMouseLeave={handleMouseLeave} /></>}</g>);
    $[7] = disabled;
    $[8] = handleClick;
    $[9] = hoveredRegion;
    $[10] = labelStyles;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    labelStyles = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
  }
  let t9;
  if ($[18] !== t4 || $[19] !== t5 || $[20] !== t6 || $[21] !== t7) {
    t9 = <svg className={t4} viewBox={t5} preserveAspectRatio={t6}>{t7}</svg>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== disabled || $[24] !== handleClick || $[25] !== hoveredRegion || $[26] !== labelStyles || $[27] !== selectedItems) {
    t10 = REGIONS.map(region_1 => {
      const _isSelected = !!selectedItems[region_1.category];
      const isHovered = hoveredRegion === region_1.category;
      const positionStyle = labelStyles[region_1.category];
      if (!positionStyle) {
        return null;
      }
      return <div key={`label-${region_1.category}`} className="pointer-events-none absolute transition-opacity duration-200" style={positionStyle}><button type="button" onClick={() => handleClick(region_1.category)} disabled={disabled} className={`ease-out-strong pointer-events-auto rounded-full px-3 py-1.5 text-xs font-medium transition-[transform,background-color,box-shadow] duration-200 ${_isSelected ? "scale-110 bg-zinc-900 text-white shadow-lg dark:bg-white dark:text-zinc-900" : isHovered ? "scale-105 bg-white/95 text-zinc-900 shadow-md dark:bg-zinc-900/95 dark:text-white" : "bg-white/80 text-zinc-700 shadow-sm hover:shadow-md dark:bg-zinc-900/80 dark:text-zinc-300"} ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`} style={BACKDROP_STYLE}>{_isSelected ? region_1.label : `+${region_1.label}`}</button></div>;
    });
    $[23] = disabled;
    $[24] = handleClick;
    $[25] = hoveredRegion;
    $[26] = labelStyles;
    $[27] = selectedItems;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10 || $[30] !== t8 || $[31] !== t9) {
    t11 = <div className={t8}>{t9}{t10}</div>;
    $[29] = t10;
    $[30] = t8;
    $[31] = t9;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  return t11;
}
function _temp(acc, region) {
  if (region.x !== undefined && region.y !== undefined) {
    acc[region.category] = {
      left: `${region.x + (region.width || 0) / 2}%`,
      top: `${region.y + (region.height || 0) / 2}%`,
      transform: "translate(-50%, -50%)"
    };
  }
  return acc;
}